import React, { useState } from 'react';

import { getDocumentRoute, Select } from '@aline/common';
import { routes } from '@aline/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack, useDocumentParams } from 'src/helpers';
import { useCheckWorkflowAuthorization, useGetCurrentDoc, useGetWorkflowFiles } from 'src/hooks';
import { useEditorLoading } from 'src/layout-engine/hooks';
import { getWorkflowWasSentForSig } from 'src/workflow';

import { DocumentHeadings } from './DocumentHeadings';
import type { OutlineMenuViewProps } from './types';

interface RemoveDocumentData {
  id: string;
  name: string;
}

export const WorkflowOutlineView: React.FC<
  Pick<
    OutlineMenuViewProps,
    'handleScrollTo' | 'headings' | 'selectedHeadingIndex' | 'areHeadingsSupported'
  > & {
    workflowId: string;
  }
> = ({ areHeadingsSupported, handleScrollTo, headings, selectedHeadingIndex, workflowId }) => {
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { document } = useGetCurrentDoc();
  const location = useLocation();
  const { workflowFiles } = useGetWorkflowFiles(workflowId);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const editorLoading = useEditorLoading();

  const [removeDocumentData, setRemoveDocumentData] = useState<RemoveDocumentData | null>(null);
  const [removeDocumentsFromWorkflow] = Apollo.useRemoveDocumentsFromWorkflowMutation();
  const dispatchDeleteSuccessSnack = useDispatchSuccessSnack({
    message: 'Document successfully removed from workflow.',
  });

  const [isHeadingsListOpen, setIsHeadingsListOpen] = useState<boolean>(true);

  const handleDocumentClick = (id: UUID): void => {
    if (id === documentId) {
      setIsHeadingsListOpen(prev => !prev);
    } else {
      setIsHeadingsListOpen(true);
      // Keep search params (e.g., to preserve ?walkThroughKey=true when in walkthrough mode).
      navigate(getDocumentRoute(id) + location.search);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!removeDocumentData) return;

    const { error } = await removeDocumentsFromWorkflow({
      variables: {
        input: { documentIds: [removeDocumentData.id], workflowId },
      },
    });
    if (!error) {
      if (documentId === removeDocumentData.id) {
        const navigateToFile = workflowFiles.find(
          file => file.document.id !== removeDocumentData.id
        );
        if (navigateToFile) {
          navigate(getDocumentRoute(navigateToFile.document.id));
        } else {
          navigate(routes.RepositoryDocuments);
        }
      }
      dispatchDeleteSuccessSnack();
    }
  };

  if (!document) return null;

  const workflowWasSentForSig = getWorkflowWasSentForSig(document.workflow);

  return (
    <>
      {workflowFiles.map((file, index) => (
        <DocumentHeadings
          key={index}
          areHeadingsSupported={areHeadingsSupported}
          dataCy={Select.document.leftOutline.fileCard}
          document={file.document}
          documentName={file.document.name}
          handleScrollTo={handleScrollTo}
          headings={headings}
          isLoading={editorLoading}
          isOpen={isHeadingsListOpen && documentId === file.document.id && !editorLoading}
          isSelected={documentId === file.document.id}
          selectedHeadingIndex={selectedHeadingIndex}
          onClick={() => handleDocumentClick(file.document.id)}
          onDelete={
            !workflowWasSentForSig && canEditWorkflow
              ? () =>
                  setRemoveDocumentData({
                    id: file.document.id,
                    name: file.document.name,
                  })
              : undefined
          }
        />
      ))}
      <ConfirmationDialog
        message={`Are you sure you want to remove the document titled "${removeDocumentData?.name}" from the workflow? Removing the document removes unused actions from workflow steps and PERMANENTLY DELETES the document. This action cannot be undone.`}
        open={Boolean(removeDocumentData)}
        title="Remove document from workflow"
        onClickPrimary={handleDeleteConfirm}
        onClose={() => setRemoveDocumentData(null)}
      />
    </>
  );
};
